import Image from 'app/Components/Shared/Image/image';
import React, { useState, useRef, useEffect } from 'react';
import './CustomTooltip.scss';
import closeIcon from './images/closeIcon.svg';
const CustomTooltip = ({ children, content, placement = 'bottom', fixedPosition = null }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [adjustedPosition, setAdjustedPosition] = useState({ placement, align: 'center' });
    const [arrowOffset, setArrowOffset] = useState(0);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            ) {
                setIsVisible(false);
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible]);

    useEffect(() => {
        if (isVisible && tooltipRef.current && triggerRef.current) {
            // Use fixed position if provided
            if (fixedPosition) {
                setArrowOffset(fixedPosition.arrowOffset || 0);
                setTooltipPosition({ top: fixedPosition.top || 0, left: fixedPosition.left || 0 });
                setAdjustedPosition({ placement: fixedPosition.placement || placement, align: 'screen-center' });
                return;
            }

            const calculatePosition = () => {
                if (!tooltipRef.current || !triggerRef.current) return;

                const tooltipRect = tooltipRef.current.getBoundingClientRect();
                const triggerRect = triggerRef.current.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const margin = 10;
                const arrowGap = 16;

                let newPlacement = placement;
                let topPosition = 0;

                if (placement === 'bottom') {
                    topPosition = triggerRect.bottom + arrowGap;
                    const bottomSpace = viewportHeight - topPosition - margin;
                    if (bottomSpace < tooltipRect.height) {
                        const topSpace = triggerRect.top - arrowGap - margin;
                        if (topSpace > bottomSpace && topSpace >= tooltipRect.height) {
                            newPlacement = 'top';
                            topPosition = triggerRect.top - arrowGap - tooltipRect.height;
                        }
                    }
                } else if (placement === 'top') {
                    topPosition = triggerRect.top - arrowGap - tooltipRect.height;
                    const topSpace = topPosition - margin;
                    if (topSpace < 0) {
                        const bottomSpace = viewportHeight - triggerRect.bottom - arrowGap - margin;
                        if (bottomSpace > topSpace) {
                            newPlacement = 'bottom';
                            topPosition = triggerRect.bottom + arrowGap;
                        }
                    }
                }

                const tooltipWidth = Math.min(tooltipRect.width, viewportWidth - 2 * margin);
                const leftPosition = (viewportWidth - tooltipWidth) / 2;

                const triggerCenter = triggerRect.left + triggerRect.width / 2;
                const tooltipCenter = leftPosition + tooltipWidth / 2;
                const offset = triggerCenter - tooltipCenter;

                setArrowOffset(offset);
                setTooltipPosition({ top: topPosition, left: leftPosition });
                setAdjustedPosition({ placement: newPlacement, align: 'screen-center' });
            };

            calculatePosition();

            window.addEventListener('scroll', calculatePosition, true);
            window.addEventListener('resize', calculatePosition);

            return () => {
                window.removeEventListener('scroll', calculatePosition, true);
                window.removeEventListener('resize', calculatePosition);
            };
        }
    }, [isVisible, placement, fixedPosition]);

    const toggleTooltip = (e) => {
        e.stopPropagation();
        setIsVisible(!isVisible);
    };

    const closeTooltip = (e) => {
        e.stopPropagation();
        setIsVisible(false);
    };

    return (
        <div className="custom-tooltip-wrapper">
            <div ref={triggerRef} onClick={toggleTooltip} className="custom-tooltip-trigger">
                {children}
            </div>
            {isVisible && (
                <div
                    ref={tooltipRef}
                    className={`custom-tooltip-content custom-tooltip-${adjustedPosition.placement} custom-tooltip-align-${adjustedPosition.align}`}
                    style={{
                        '--arrow-offset': `${arrowOffset}px`,
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`
                    }}
                >
                    <button className="custom-tooltip-close" onClick={closeTooltip}>
                        <Image src={closeIcon} alt="close" />
                    </button>
                    <div className="custom-tooltip-text">{content}</div>
                </div>
            )}
        </div>
    );
};

export default CustomTooltip;
